/**
 * Simple encryption/decryption utilities for localStorage
 * Uses AES-like encryption with a secret key
 *
 * NOTE: This provides obfuscation, not military-grade security.
 * For maximum security, use HTTP-only cookies and server-side sessions.
 */
import { logger } from './logger'

const SECRET_KEY = import.meta.env.VITE_STORAGE_ENCRYPTION_KEY || 'bbg-default-secret-key-change-in-production'

/**
 * Encrypt data to store in localStorage
 */
export function encryptData(data: string): string {
  try {
    // Convert data to base64
    const base64 = btoa(data)

    // Simple XOR encryption with key
    let encrypted = ''
    for (let i = 0; i < base64.length; i++) {
      const charCode = base64.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length)
      encrypted += String.fromCharCode(charCode)
    }

    // Convert to base64 again for safe storage
    return btoa(encrypted)
  } catch (error) {
    logger.error('Encryption failed:', error)
    return data // Fallback to unencrypted
  }
}

/**
 * Decrypt data from localStorage
 */
export function decryptData(encryptedData: string): string {
  try {
    // Decode from base64
    const encrypted = atob(encryptedData)

    // XOR decryption with key
    let decrypted = ''
    for (let i = 0; i < encrypted.length; i++) {
      const charCode = encrypted.charCodeAt(i) ^ SECRET_KEY.charCodeAt(i % SECRET_KEY.length)
      decrypted += String.fromCharCode(charCode)
    }

    // Decode from base64
    return atob(decrypted)
  } catch (error) {
    // Don't log - stores will handle cleanup silently
    throw error
  }
}

/**
 * Enhanced encryption using Web Crypto API (browser native)
 * More secure but async - use for sensitive data
 */
export async function encryptDataSecure(data: string): Promise<string> {
  try {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data)

    // Generate a key from the secret
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(SECRET_KEY.padEnd(32, '0').slice(0, 32)),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    )

    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode('bbg-salt'),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    )

    // Generate random IV
    const iv = crypto.getRandomValues(new Uint8Array(12))

    // Encrypt
    const encryptedBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, dataBuffer)

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength)
    combined.set(iv, 0)
    combined.set(new Uint8Array(encryptedBuffer), iv.length)

    // Convert to base64
    return btoa(String.fromCharCode(...combined))
  } catch (error) {
    logger.error('Secure encryption failed:', error)
    return encryptData(data) // Fallback to simple encryption
  }
}

/**
 * Enhanced decryption using Web Crypto API
 */
export async function decryptDataSecure(encryptedData: string): Promise<string> {
  try {
    const encoder = new TextEncoder()
    const decoder = new TextDecoder()

    // Decode from base64
    const combined = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0))

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12)
    const data = combined.slice(12)

    // Generate key
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(SECRET_KEY.padEnd(32, '0').slice(0, 32)),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    )

    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode('bbg-salt'),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    )

    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data)

    return decoder.decode(decryptedBuffer)
  } catch (error) {
    logger.error('Secure decryption failed:', error)
    return decryptData(encryptedData) // Fallback to simple decryption
  }
}
